import { RotateCw } from 'lucide-react';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Shared dashboard widget states (T-102, design `A01-home-loading` / `A01-home-error`): a
 * skeleton shaped like the card's content, and a danger Banner whose Retry calls the widget
 * query's `refetch`.
 */

/** Error state: titled danger Banner with a Retry button that refetches the widget's query. */
export function WidgetError({
  onRetry,
  retrying = false,
}: {
  onRetry: () => void;
  retrying?: boolean;
}) {
  return (
    <Banner
      tone="danger"
      title="We couldn’t load this card"
      action={
        <Button size="sm" onClick={onRetry} loading={retrying}>
          <RotateCw aria-hidden="true" />
          Retry
        </Button>
      }
    >
      Something went wrong on our side. Nothing you saved has been lost.
    </Banner>
  );
}

/** Loading state of a stat-tile grid: `count` tiles in the given column layout. */
export function StatTilesSkeleton({
  count,
  className = 'grid-cols-2',
}: {
  count: number;
  className?: string;
}) {
  return (
    <div aria-busy="true" className={cn('grid gap-2', className)}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex flex-col gap-2 rounded-lg bg-surface-muted px-3.5 py-3">
          <Skeleton className="h-2.5 w-1/2 bg-border" />
          <Skeleton className="h-6 w-2/3 bg-border" />
          <Skeleton className="h-2.5 w-3/5 bg-border" />
        </div>
      ))}
    </div>
  );
}

/** Loading state of a list: `count` lines (title + detail). */
export function RowsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div aria-busy="true" className="flex flex-col gap-3">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="size-10 shrink-0 rounded-lg" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3.5 w-3/5" />
            <Skeleton className="h-2.5 w-2/5" />
          </div>
        </div>
      ))}
    </div>
  );
}
